import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Config } from '../app.configs';
import { PaginatedDebts } from '../interfaces/paginated-debt.interface';
import { CacheService } from './cache.service';

export interface ReturnTransactionPayload {
  amount: number;
  recipient_uuid?: string;
  note?: string;
}

@Injectable({
  providedIn: 'root',
})
export class DebtService {
  private readonly CACHE_PREFIX_DEBTS = 'debts:';

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private cacheService: CacheService,
  ) {}

  getDebts(
    uuid: string,
    page: number = 1,
    forceRefresh: boolean = false,
  ): Observable<PaginatedDebts> {
    const cacheKey = `${this.CACHE_PREFIX_DEBTS}${uuid}:${page}`;

    return this.cacheService.get<PaginatedDebts>(
      cacheKey,
      () => {
        const params = new HttpParams().set('page', page);
        const headers = this.authService.getHeaders();
        return this.http.get<PaginatedDebts>(
          Config.toApiUrl(`dashboard/users/${uuid}/debts/`),
          { headers, params, withCredentials: true },
        );
      },
      undefined,
      forceRefresh,
    );
  }

  invalidateDebtsCache(uuid: string): void {
    this.cacheService.invalidateByPrefix(`${this.CACHE_PREFIX_DEBTS}${uuid}`);
  }

  /**
   * Invalidate all debts caches (use when user UUID is unknown, e.g., transaction-based operations)
   */
  invalidateAllDebtsCache(): void {
    this.cacheService.invalidateByPrefix(this.CACHE_PREFIX_DEBTS);
  }

  refundTransaction(uuid: string, type: string, amount = 0): Observable<any> {
    const headers = this.authService.getHeaders();

    const payload = {
      type: type,
      amount: amount,
    };

    return this.http
      .post(
        Config.toApiUrl(`dashboard/transactions/${uuid}/refunds/`),
        payload,
        { headers, withCredentials: true },
      )
      .pipe(tap(() => this.invalidateAllDebtsCache()));
  }

  returnTransaction(
    uuid: string,
    payload: ReturnTransactionPayload,
  ): Observable<any> {
    const headers = this.authService.getHeaders();

    return this.http
      .post(
        Config.toApiUrl(`dashboard/transactions/${uuid}/returns/`),
        payload,
        { headers, withCredentials: true },
      )
      .pipe(tap(() => this.invalidateAllDebtsCache()));
  }

  clearDebts(
    uuid: string,
    payload: { reason?: string; comment?: string } = {},
  ): Observable<any> {
    const headers = this.authService.getHeaders();

    return this.http
      .post(
        Config.toApiUrl(`dashboard/users/${uuid}/clear-debts/`),
        payload,
        { headers, withCredentials: true },
      )
      .pipe(tap(() => this.invalidateDebtsCache(uuid)));
  }

  clearDebt(
    uuid: string,
    debtId: string | number,
    payload: { reason?: string; comment?: string } = {},
  ): Observable<any> {
    const headers = this.authService.getHeaders();

    return this.http
      .post(
        Config.toApiUrl(`dashboard/users/${uuid}/debts/${debtId}/clear/`),
        payload,
        { headers, withCredentials: true },
      )
      .pipe(tap(() => this.invalidateDebtsCache(uuid)));
  }
}
